import {FC, useState, useEffect} from 'react'
import {useDropzone} from 'react-dropzone'
import {ReactSortable} from 'react-sortablejs'
import uuid from 'react-uuid'
import styles from './ImageUpload.module.css'

interface FileWithPreview extends File {
  preview: string
  id: string | number
}

type ImageType = {
  id: number
  productId: string
  path: string
  imgsuffix: string
  typeimg: string
}

interface ImageUploadProps {
  label?: string
  iconImage?: string
  mainTextImage?: string
  descriptionImage?: string
  onChange?: (files: FileWithPreview[] | ImageType[]) => void
  sizeImage?: string
  dragAndDropImage?: boolean
  verticalAlign?: boolean
  maxFiles?: number
  showThumbs?: boolean
  requiredWidth?: number
  requiredHeight?: number
  onSizeError?: (error: string) => void
  value: FileWithPreview[] | ImageType[]

  setImagesToDelete?: (value: any[]) => void
  imagesToDelete?: any[]
}

export const ImageUploadEdit: FC<ImageUploadProps> = ({
  label,
  onChange,
  iconImage,
  descriptionImage,
  mainTextImage = 'Arraste e solte sua imagem aqui',
  sizeImage,
  dragAndDropImage = false,
  maxFiles,
  showThumbs = true,
  value,
  requiredWidth = 640,
  requiredHeight = 640,
  onSizeError,
  verticalAlign,
  setImagesToDelete,
  imagesToDelete = [],
}) => {
  const [files, setFiles] = useState<FileWithPreview[] | ImageType[]>(value)

  // Sincroniza o estado local com o valor vindo do componente pai
  useEffect(() => {
    // Verifica se o valor realmente mudou para evitar loops infinitos
    if (JSON.stringify(value) !== JSON.stringify(files)) {
      setFiles(value)
    }
  }, [value, files])

  const {getRootProps, getInputProps} = useDropzone({
    accept: {
      'image/*': [],
    },
    maxFiles: maxFiles || undefined,
    onDrop: (acceptedFiles) => {
      if (maxFiles && files.length + acceptedFiles.length > maxFiles) {
        const remainingSlots = maxFiles - files.length
        acceptedFiles = acceptedFiles.slice(0, remainingSlots)
      }

      const newFiles = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          id: uuid(),
        })
      )

      if (newFiles.length > 0) {
        const updatedFiles = [...(files as FileWithPreview[]), ...newFiles]
        setFiles(updatedFiles)
        if (onChange) {
          onChange(updatedFiles)
        }
      }
    },
  })

  const deleteImage = (id: any) => {
    if (Array.isArray(files) && files.length > 0) {
      // Verifica se todos os arquivos são FileWithPreview
      const allAreFileWithPreview = (files as any).every((file: any) => 'preview' in file)
      // Verifica se todos os arquivos são ImageType
      const allAreImageType = (files as any).every((file: any) => !('preview' in file))

      if (allAreFileWithPreview) {
        const updatedFiles = (files as FileWithPreview[]).filter((file) => file.id !== id)
        setFiles(updatedFiles as FileWithPreview[])
        if (onChange) {
          onChange(updatedFiles as FileWithPreview[])
        }
      } else if (allAreImageType) {
        const updatedFiles = (files as ImageType[]).filter((file) => file.id !== id)

        const find = (files as any).find((file: any) => file.id === id)

        const newDelete = [...imagesToDelete, find]

        setImagesToDelete && setImagesToDelete(newDelete)

        setFiles(updatedFiles as ImageType[])
        if (onChange) {
          onChange(updatedFiles as ImageType[])
        }
      } else {
        // Caso misto, remove pelo id independentemente do tipo
        const updatedFiles = (files as any).filter((file: any) => file.id !== id)

        const find = (files as any).find((file: any) => file.id === id)

        if (find && find.path && find.imgsuffix) {
          const newDelete = [...imagesToDelete, find]
          setImagesToDelete && setImagesToDelete(newDelete)
        }

        setFiles(updatedFiles)
        if (onChange) {
          onChange(updatedFiles)
        }
      }
    } else {
      // fallback para array vazio ou não array
      setFiles([] as typeof files)
      if (onChange) {
        onChange([] as typeof files)
      }
    }
  }

  const handleSort = (newFiles: any[]) => {
    // Atualiza o estado local primeiro
    setFiles(newFiles)
    // Depois notifica o componente pai
    if (onChange) {
      onChange(newFiles)
    }
  }

  let thumbs: JSX.Element[] = []
  if (Array.isArray(files) && files.length > 0) {
    thumbs = (files as any).map((file: any, index: number) => {
      if ('preview' in file && file.preview) {
        const imgSrc = file.preview
        const handleImgLoad = () => {
          URL.revokeObjectURL(file.preview)
        }
        return (
          <div
            className={`${styles.thumb} ${
              dragAndDropImage && index === 0 ? styles.firstThumb : ''
            } ${dragAndDropImage ? styles.draggableThumb : ''}`}
            key={file.id}
          >
            <div className={styles.thumbInner}>
              <div className={styles.thumbImgContainer}>
                <img src={imgSrc} className={`${styles.thumbImg}`} onLoad={handleImgLoad} alt='' />
                <i
                  className={`fas fa-times ${styles.iconThumbDelete}`}
                  onClick={() => deleteImage(file.id)}
                ></i>
              </div>
              {dragAndDropImage && index === 0 && (
                <span className={styles.firstThumbLabel}>Principal</span>
              )}
            </div>
          </div>
        )
      } else {
        const imgSrc = `${(file as ImageType).path}/${(file as ImageType).imgsuffix}`
        return (
          <div
            className={`
                        ${styles.thumb}
                        ${dragAndDropImage && index === 0 ? styles.firstThumb : ''}
                        ${dragAndDropImage ? styles.draggableThumb : ''}
                        `}
            key={file.id}
          >
            <div className={styles.thumbInner}>
              <div className={styles.thumbImgContainer}>
                <img src={imgSrc} className={`${styles.thumbImg}`} alt='' />
                <i
                  className={`fas fa-times ${styles.iconThumbDelete}`}
                  onClick={() => deleteImage(file.id)}
                ></i>
              </div>
              {dragAndDropImage && index === 0 && (
                <span className={styles.firstThumbLabel}>Principal</span>
              )}
            </div>
          </div>
        )
      }
    })
  }

  useEffect(() => {
    return () => {
      files.forEach((file: any) => {
        if ('preview' in file && file.preview) {
          URL.revokeObjectURL(file.preview)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files && showThumbs])

  return (
    <div>
      {label && <label className='form-label fw-normal'>{label}</label>}
      <section className='row px-3'>
        <div
          {...getRootProps({className: verticalAlign ? styles.verticalDropzone : styles.dropzone})}
        >
          <input {...getInputProps()} />
          {iconImage && <i className={`bi ${iconImage} ${styles.dropzoneIcon}`}></i>}
          {mainTextImage && <p className={styles.dropzoneMainText}>{mainTextImage}</p>}
          <p className={styles.dropzoneSubText}>
            Para maior qualidade envie imagens no formato JPG ou PNG
          </p>
          {sizeImage && <p className={styles.sizeImageText}>{sizeImage}</p>}
        </div>
        {showThumbs && (
          <>
            {dragAndDropImage && Array.isArray(files) && files.length > 0 ? (
              <ReactSortable<FileWithPreview | ImageType>
                list={files as (FileWithPreview | ImageType)[]}
                setList={handleSort}
                className={verticalAlign ? styles.verticalThumbContainer : styles.thumbsContainer}
                animation={150}
              >
                {thumbs}
              </ReactSortable>
            ) : (
              <aside
                className={verticalAlign ? styles.verticalThumbContainer : styles.thumbsContainer}
              >
                {thumbs}
              </aside>
            )}
            {thumbs.length > 0 && descriptionImage && (
              <div>
                <p className={styles.descriptionImage}>{descriptionImage}</p>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}
